import type { Driver } from '@cobliteam/api-lib/src/dashboard/driver';
import type { UUID } from '@cobliteam/common-types';
import { useGetPaths } from '@cobliteam/hooks/src/usePathAssociation';
import Alert from '@cobliteam/ui/components/Alert';
import DatePicker, { type DateRange } from '@cobliteam/ui/components/DatePicker';
import DataTable, { filterGetByKeys, filterGetParamDates, filterSetParamDates, filterSetParamFilters, type IFiltersDef } from '@cobliteam/ui-business/components/DataTable';
import { toIdentificationMethods } from '@cobliteam/ui-business/components/MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';
import { differenceInDays, endOfDay, startOfDay } from '@cobliteam/utils/date';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PathSelectAction from '../PathSelectAction';
import columnsDef from './columnsDef';
import exportDef from './exportDef';

const LIMIT_IN_DAYS = 7;

const VehiclePathsTable = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const initialDate = filterGetParamDates(1, 1);
  const [selectedDate, setSelectedDate] = useState<DateRange>({
    from: new Date(initialDate.startDate),
    to: new Date(initialDate.endDate),
  });
  const [associationMethods, setAssociationMethods] = useState<string[] | undefined>(undefined);
  const [driverIds, setDriverIds] = useState<UUID[]>([]);

  const filterDate = useMemo(
    () => ({
      start: startOfDay(selectedDate.from ?? new Date()).getTime(),
      end: endOfDay(selectedDate.to ?? new Date()).getTime(),
    }),
    [selectedDate]
  );
  const filtersDef = filterGetByKeys(['MultiSelectAssociationSource', 'MultiSelectDrivers']);

  const handleFilterChange = (filter: IFiltersDef) => {
    const filterDef = filtersDef.find((f) => f.key === filter.key) as IFiltersDef;
    const newFilter = {
      ...filterDef,
      ...filter,
    };

    filterSetParamFilters(newFilter);

    if (filter.key === 'MultiSelectAssociationSource') {
      const identificationMethods = toIdentificationMethods(filter.value);
      setAssociationMethods(identificationMethods.length > 0 ? identificationMethods : undefined);
    }

    if (filter.key === 'MultiSelectDrivers') {
      const driverIds = filter.value.map((driver: Driver) => driver.id) || [];
      setDriverIds(driverIds);
    }
  };

  const {
    data: paths,
    isLoading: isPathsLoading,
    isFetching: isPathsFetching,
    refetch: refetchPaths,
  } = useGetPaths(vehicleId || '', filterDate.start, filterDate.end, associationMethods, { enabled: !!vehicleId });

  const filteredPaths = driverIds.length > 0 ? paths?.filter((path) => path.driver?.id && driverIds.includes(path.driver?.id)) : paths;

  const pathsWithFallback = filteredPaths || [];
  const licensePlate = (pathsWithFallback.length > 0 && pathsWithFallback[0].vehicle?.licensePlate) || undefined;
  const exportTitle = `Relatório de associação de trechos${licensePlate ? ` - ${licensePlate}` : ''}`;

  const handleDateChange = (date: DateRange) => {
    const startDate = date.from ?? new Date();
    const endDate = date.to ?? new Date();

    if (differenceInDays(endDate, startDate) >= LIMIT_IN_DAYS) {
      Alert.error('O período selecionado é maior que 7 dias');
    } else {
      setSelectedDate(date);
      filterSetParamDates({
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  return (
    <DataTable
      id='vehicle-paths-table'
      columnsDef={columnsDef}
      filtersDef={filtersDef}
      exportDef={exportDef(exportTitle)}
      data={filteredPaths}
      search={false}
      onChangeFilter={handleFilterChange}
      datePicker={
        <DatePicker
          mode='range'
          selected={selectedDate}
          onSelect={handleDateChange}
          required
          disabledDays={{ after: new Date() }}
          presets={['today', 'yesterday', 'last-7-days']}
          max={LIMIT_IN_DAYS}
        />
      }
      isLoading={(isPathsLoading || isPathsFetching || !vehicleId) && !paths}
      isFetching={isPathsFetching}
      onRefresh={refetchPaths}
      sortingDef={[{ id: 'date', desc: false }]}
      selectedActions={(table, columnsDef) => <PathSelectAction table={table} columnsDef={columnsDef} onApply={refetchPaths} />}
    />
  );
};

export default VehiclePathsTable;
